import { useState } from "react";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtTons } from "@/lib/format";
import { STATUS, DECISION, slugify } from "./imageUtils";
import { CorrectionPanel } from "./CorrectionPanel";
import { EntryEditor } from "./EntryEditor";
import { Pencil, Wrench, PlusCircle } from "lucide-react";

const FILTERS = [["todo", "To do"], ["all", "All"], ["mismatch"], ["needs_check"], ["missing_on_website"], ["missing_in_book"], ["matched"], ["resolved", "Resolved"]];
const isTodo = (r) => r.status === "needs_check" || (r.status !== "matched" && !r.resolved);

const Summary = ({ slug, s }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-3">
    {Object.keys(STATUS).filter((k) => k !== "deleted").map((k) => (
      <div key={k} data-testid={`summary-${slug}-${k}`} className={`rounded-lg border px-3 py-2 ${STATUS[k].cls}`}>
        <div className="text-[11px] uppercase tracking-wide leading-tight">{STATUS[k].label}</div><div className="text-xl font-black">{s[k]}</div>
      </div>
    ))}
    <div className="rounded-lg border bg-white px-3 py-2"><div className="text-[11px] uppercase text-slate-500">Corrected</div><div data-testid={`summary-${slug}-corrected`} className="text-xl font-black">{s.corrected ?? 0}</div></div>
    <div className="rounded-lg border bg-white px-3 py-2"><div className="text-[11px] uppercase text-slate-500">Open issues</div><div data-testid={`summary-${slug}-open`} className="text-xl font-black">{s.open_discrepancies ?? 0}</div></div>
    <div className="rounded-lg border bg-white px-3 py-2"><div className="text-[11px] uppercase text-slate-500">Book / Website t</div><div data-testid={`summary-${slug}-tons`} className="text-base font-black">{fmtTons(s.book_tons)} / {fmtTons(s.web_tons)}</div></div>
  </div>
);

const Row = ({ r, ctx }) => {
  const [mode, setMode] = useState(null);
  const [editId, setEditId] = useState(null);
  const d = r.decision;
  const entries = (ctx.entries || []).filter((e) => r.book_entry_ids.includes(e.id));
  const done = () => { setMode(null); setEditId(null); ctx.onChange(); };
  return (
    <li data-testid={`result-row-${r.key}`} className="py-3 border-b border-slate-100">
      <div className="grid grid-cols-2 xl:grid-cols-[110px_minmax(0,1fr)_90px_90px_80px_minmax(160px,1.2fr)] gap-x-3 gap-y-1 items-start text-sm">
        <div className="text-slate-600">{r.date ? fmtDate(r.date) : "?"}</div>
        <div className="font-semibold text-right xl:text-left break-words min-w-0">{r.customer}</div>
        <div className="xl:text-right">Book: <b className="tabular-nums">{r.book_tons == null ? "—" : fmtTons(r.book_tons)}</b>{r.book_text?.includes("+") && <div className="text-[11px] text-slate-500">{r.book_text}</div>}</div>
        <div className="text-right">Web: <b className="tabular-nums">{r.web_tons == null ? "—" : fmtTons(r.web_tons)}</b></div>
        <div className={`xl:text-right tabular-nums font-semibold ${r.diff ? "text-red-600" : "text-slate-400"}`}>{r.diff == null ? "" : (r.diff > 0 ? "+" : "") + r.diff.toFixed(3)}</div>
        <div className="text-right xl:text-left min-w-0 break-words">
          <span className={`inline-block text-xs border rounded px-2 py-0.5 ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span>
          {d && <div data-testid={`decision-${r.key}`} className={`text-[11px] mt-0.5 ${d.status === "failed" ? "text-red-700" : "text-emerald-700"}`}>{d.status === "failed" ? `Failed: ${d.error} — you can retry` : `${DECISION[d.action]} · ${d.approved_by}`}</div>}
          {r.reasons.map((x) => <div key={x} className="text-[11px] text-slate-500 mt-0.5">{x}</div>)}
        </div>
      </div>
      {!ctx.readOnly && (
        <div className="flex flex-wrap gap-2 mt-2">
          {entries.length > 0 && r.status !== "matched" && <Button type="button" size="sm" variant="outline" className="h-10" data-testid={`edit-book-${r.key}`} onClick={() => setMode(mode === "book" ? null : "book")}><Pencil className="h-3 w-3 mr-1" />Correct AI reading</Button>}
          {r.status === "missing_in_book" && <Button type="button" size="sm" variant="outline" className="h-10" data-testid={`add-book-${r.key}`} onClick={() => setMode(mode === "add" ? null : "add")}><PlusCircle className="h-3 w-3 mr-1" />AI missed a notebook line</Button>}
          {["mismatch", "missing_on_website", "missing_in_book"].includes(r.status) && !r.resolved && <Button type="button" size="sm" className="h-10 bg-slate-900" data-testid={`fix-row-${r.key}`} onClick={() => setMode(mode === "fix" ? null : "fix")}><Wrench className="h-3 w-3 mr-1" />Resolve</Button>}
        </div>
      )}
      {mode === "fix" && <CorrectionPanel sessionId={ctx.sessionId} plant={ctx.plant} row={r} onDone={done} onCancel={() => setMode(null)} />}
      {mode === "add" && <div className="mt-2"><EntryEditor sessionId={ctx.sessionId} plant={ctx.plant} customers={ctx.customers} defaults={{ date: r.date, customer_read: r.customer, matched_customer: r.customer }} onDone={done} onCancel={() => setMode(null)} /></div>}
      {mode === "book" && (
        <ul className="mt-2 space-y-2">
          {entries.map((e) => editId === e.id
            ? <li key={e.id}><EntryEditor entry={e} sessionId={ctx.sessionId} plant={ctx.plant} customers={ctx.customers} onDone={done} onCancel={() => setEditId(null)} /></li>
            : <li key={e.id} className="flex flex-wrap items-center gap-2 text-sm bg-white border rounded-md p-2">
                <span>Page {e.page ?? "manual"} · {e.date ? fmtDate(e.date) : "?"} · {e.customer_read} — <b>{e.tons_text}</b></span>
                <Button type="button" size="sm" variant="outline" className="h-10 ml-auto" data-testid={`edit-entry-${e.id}`} onClick={() => setEditId(e.id)}>Edit</Button>
              </li>)}
        </ul>
      )}
    </li>
  );
};

export const PlantResults = ({ plant, data, readOnly, sessionId, entries, customers, onChange }) => {
  const [filter, setFilter] = useState(readOnly ? "all" : "todo");
  const slug = slugify(plant);
  const rows = data.rows.filter((r) => filter === "all" || (filter === "todo" ? isTodo(r) : filter === "resolved" ? (r.resolved && r.status !== "matched") || r.decision?.status === "applied" : r.status === filter));
  const ctx = { readOnly, sessionId, plant, entries, customers, onChange };
  return (
    <section data-testid={`results-${slug}`} className="bg-white border rounded-xl p-3 sm:p-4">
      <h3 className="font-chivo font-black text-xl mb-3">{plant}</h3>
      <Summary slug={slug} s={data.summary} />
      <div className="flex flex-wrap gap-1.5 mb-2">
        {FILTERS.map(([f, label]) => (
          <button type="button" key={f} data-testid={`filter-${slug}-${f}`} onClick={() => setFilter(f)}
            className={`text-xs sm:text-sm px-3 py-2 rounded-full border ${filter === f ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>
            {label || STATUS[f].label}
          </button>
        ))}
      </div>
      <ul>{rows.map((r) => <Row key={r.key} r={r} ctx={ctx} />)}</ul>
      {rows.length === 0 && <p className="text-sm text-slate-400 p-3">Nothing here.</p>}
    </section>
  );
};
